/**
 * Utility functions for detecting and handling chunk load errors that occur
 * when the app is redeployed and old chunk files are no longer available.
 */

const CHUNK_ERROR_REFRESH_KEY = 'chunk_error_refresh_attempted';
const CHUNK_ERROR_TIMESTAMP_KEY = 'chunk_error_refresh_timestamp';
const REFRESH_COOLDOWN_MS = 10000; // 10 seconds

/**
 * Checks if an error is a chunk load error (failed dynamic import)
 * These errors occur when lazy-loaded modules fail to load, typically after a deployment
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  const errorMessage = error instanceof Error ? error.message : String(error);

  // Check for common chunk load error patterns across different browsers
  const chunkErrorPatterns = [
    /Failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /error loading dynamically imported module/i,
    /ChunkLoadError/i,
    /Loading chunk [\d]+ failed/i,
    /Failed to fetch/i,
  ];

  return chunkErrorPatterns.some(pattern => pattern.test(errorMessage));
}

/**
 * Checks if we should refresh the page for a chunk error
 * Returns false if we've already refreshed recently or in this session
 */
export function shouldRefreshForChunkError(): boolean {
  try {
    // Check if we've already attempted a refresh in this session
    const refreshAttempted = sessionStorage.getItem(CHUNK_ERROR_REFRESH_KEY);
    if (refreshAttempted === 'true') {
      return false;
    }

    // Check cooldown period to prevent rapid refreshes
    const lastRefreshStr = sessionStorage.getItem(CHUNK_ERROR_TIMESTAMP_KEY);
    if (lastRefreshStr) {
      const lastRefresh = parseInt(lastRefreshStr, 10);
      const timeSinceRefresh = Date.now() - lastRefresh;

      if (timeSinceRefresh < REFRESH_COOLDOWN_MS) {
        return false;
      }
    }

    return true;
  } catch (error) {
    // If sessionStorage is not available, allow refresh
    console.error('Error checking chunk error refresh status:', error);
    return true;
  }
}

/**
 * Marks that we've attempted a refresh for a chunk error
 * This prevents infinite refresh loops
 */
export function markChunkErrorRefresh(): void {
  try {
    sessionStorage.setItem(CHUNK_ERROR_REFRESH_KEY, 'true');
    sessionStorage.setItem(CHUNK_ERROR_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Error marking chunk error refresh:', error);
  }
}

/**
 * Clears the chunk error refresh flag
 * Should be called on successful app load to allow future refreshes if needed
 */
export function clearChunkErrorFlag(): void {
  try {
    sessionStorage.removeItem(CHUNK_ERROR_REFRESH_KEY);
    sessionStorage.removeItem(CHUNK_ERROR_TIMESTAMP_KEY);
  } catch (error) {
    console.error('Error clearing chunk error flag:', error);
  }
}
